/**
 * Declared list of the farm risk SCALARS the staff UI can explicitly remove, plus the merge fixup
 * every write site has to apply.
 *
 * Purpose: the farm merge and delta model is built on "a null/empty source means the payload did not
 * carry this field, so keep what is saved". That is right for almost everything, and it is what
 * `deepmerge`'s `filterValues`, `recursiveDeepMerge` and `applyFarmObjectDelta` all implement. It is
 * wrong for the handful of fields behind a "remove coverage" trash-can in staff-fe, whose entire
 * removal gesture IS sending null — under the general rule the removal silently no-ops and the
 * coverage the agent just deleted comes back on the next re-rate (#32149, #32150, #34975).
 *
 * Flow: four sites have to agree, and each one previously decided for itself.
 *   1. rater-v2 `offer.manager` — post-`deepmerge` fixup on the offer update path.
 *   2. policy-mod `farm-segment.manager` — the same fixup on the endorsement path.
 *   3. policy-mod `recursiveDeepMerge` — accumulating a new change onto an already-stored delta.
 *   4. farm-utils `applyFarmObjectDelta` — replaying a stored delta back onto the base segment.
 * Site 1 was fixed alone in #32250; sites 2-4 kept dropping the null, so removal still didn't stick
 * on a policy endorsement. This module is the single answer, mirroring what
 * `FE_AUTHORITATIVE_ARRAY_FIELD_NAMES` already does for the arrays alongside these fields.
 *
 * Key decision: an allowlist rather than "honour every null". Making nulls meaningful everywhere
 * would change delta replay for every farm field on every in-force policy — that is a rating-visible
 * change needing its own production scan. The general limitation therefore stands (see the
 * `entity.name` case pinned in `farm-segment-round-trip.test.ts`); this is the declared exception.
 *
 * Limit: entries are keyed by bare field name, matching the dispatch in `applyFarmObjectDelta` and
 * `recursiveDeepMerge`, so a name reachable under two parent types gets one answer for both. The
 * names here are specific enough that that is not a live concern.
 *
 * Issue: #34975. Prior art: #32250 (offer path alone), #34355 (the array twin).
 */

/** Well-pump replacement value — staff-fe `well-pump.jsx`, stored on `risk.building`. */
export const REMOVABLE_RISK_BUILDING_FIELDS = [
  'numWellPumps',
  'wellPumpEstimatedValue',
  'wellPumpDescription'
] as const;

/** Dwelling under construction (theft) — staff-fe `dwelling-under-construction.jsx`, on `risk.coverages`. */
export const REMOVABLE_RISK_COVERAGE_FIELDS = [
  'resUnderConstructionLimit',
  'resUnderConstructionEffectiveDate'
] as const;

/**
 * Underwriter protection-class override (#35212) — LOCATION-level scalars, staff-fe
 * `protection-class-override.jsx`. Same landmine: the FE "clear override" gesture sends
 * `fpcOverrideValue`/`fpcOverrideReason`/`fpcOverrideBy` as `null`, which the general "null source =
 * field absent, keep saved" rule would drop, leaving stale audit values behind after the override is
 * cleared. Unlike the risk fields above these live directly on the LOCATION (not `risk.building`/
 * `risk.coverages`), so they get their own apply helper — but they share the bare-name predicate the
 * delta-replay sites use. `fpcOverride` itself clears to `false` (not null) and needs no fixup, but is
 * listed so the whole override travels together.
 */
export const REMOVABLE_LOCATION_SCALAR_FIELD_NAMES = [
  'fpcOverride',
  'fpcOverrideValue',
  'fpcOverrideReason',
  'fpcOverrideBy'
] as const;

/** Every removable scalar, for the two sites that dispatch on a bare field name. */
export const REMOVABLE_RISK_SCALAR_FIELD_NAMES: readonly string[] = Object.freeze([
  ...REMOVABLE_RISK_BUILDING_FIELDS,
  ...REMOVABLE_RISK_COVERAGE_FIELDS
]);

const REMOVABLE_SCALAR_FIELD_SET = new Set<string>([
  ...REMOVABLE_RISK_SCALAR_FIELD_NAMES,
  ...REMOVABLE_LOCATION_SCALAR_FIELD_NAMES
]);

/**
 * True when an explicit `null` for this field means "the agent removed this coverage / cleared this
 * override" rather than "the payload didn't carry this field". Covers both the risk-level removable
 * scalars and the location-level fpc-override scalars, since both delta-replay sites dispatch on a
 * bare field name.
 */
export function isRemovableRiskScalarField(fieldName: string | undefined | null): boolean {
  return !!fieldName && REMOVABLE_SCALAR_FIELD_SET.has(fieldName);
}

/**
 * Assign every removable scalar the payload carries, overriding whatever `deepmerge` produced.
 *
 * Presence, not truthiness: `Object.hasOwn`-style detection is the whole point, because the value
 * being assigned is usually `null`. A field the FE omits stays at the merged (saved) value.
 *
 * Both the offer updater and the policy-mod endorsement updater call this, so they share the *logic*
 * and not merely the field list — sharing only the list still let a fix land on one copy and not its
 * twin, which is the drift that produced #34355 and, here, #34975.
 *
 * `hasOwnProperty` rather than `Object.hasOwn`: policy-mod pins es2021.
 */
export function applyFeAuthoritativeScalarFields(
  targetRisk: Record<string, any> | null | undefined,
  submittedRisk: Record<string, any> | null | undefined
): void {
  if (!targetRisk || !submittedRisk) {
    return;
  }

  assignDeclaredFields(targetRisk.building, submittedRisk.building, REMOVABLE_RISK_BUILDING_FIELDS);
  assignDeclaredFields(targetRisk.coverages, submittedRisk.coverages, REMOVABLE_RISK_COVERAGE_FIELDS);
}

/**
 * The location-level twin of {@link applyFeAuthoritativeScalarFields} for the protection-class override
 * (#35212): assign every fpc-override scalar the payload carries directly onto the location, overriding
 * whatever `deepmerge` produced, so clearing the override (null value/reason/by) actually persists. The
 * offer updater and the policy-mod endorsement updater both call this on their location merge.
 */
export function applyFeAuthoritativeLocationScalarFields(
  targetLocation: Record<string, any> | null | undefined,
  submittedLocation: Record<string, any> | null | undefined
): void {
  assignDeclaredFields(targetLocation, submittedLocation, REMOVABLE_LOCATION_SCALAR_FIELD_NAMES);
}

function assignDeclaredFields(
  target: Record<string, any> | null | undefined,
  submitted: Record<string, any> | null | undefined,
  fields: readonly string[]
): void {
  if (!target || !submitted) {
    return;
  }

  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(submitted, field)) {
      target[field] = submitted[field];
    }
  }
}
